// src/services/stockAlertsService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class StockAlertsService {
  // 🚨 Get all stock alerts
  async getAllStockAlerts(params = {}) {
    try {
      console.log('🚨 Fetching all stock alerts with params:', params)
      const response = await ApiService.get(AppURLs.STOCK_ALERTS.GET_ALL, params)
      console.log('✅ Stock alerts fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching stock alerts:', error)
      throw error
    }
  }

  // 🚨 Get stock alerts by branch
  async getStockAlertsByBranch(branchId, params = {}) {
    try {
      console.log('🚨 Fetching stock alerts for branch:', branchId)
      const queryParams = { branch: branchId, ...params }
      const response = await ApiService.get(AppURLs.STOCK_ALERTS.GET_ALL, queryParams)
      console.log('✅ Branch stock alerts fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branch stock alerts:', error)
      throw error
    }
  }

  // 📊 Get stock alerts statistics
  async getStockAlertsStats(branchId = null) {
    try {
      console.log('📊 Fetching stock alerts statistics for branch:', branchId)
      const params = branchId ? { branch: branchId } : {}
      const response = await ApiService.get(AppURLs.STOCK_ALERTS.STATS, params)
      console.log('✅ Stock alerts statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching stock alerts statistics:', error)
      throw error
    }
  }

  // 📊 Get stock alerts summary
  async getStockAlertsSummary(branchId = null) {
    try {
      console.log('📊 Fetching stock alerts summary for branch:', branchId)
      const params = branchId ? { branch: branchId } : {}
      const response = await ApiService.get(AppURLs.STOCK_ALERTS.SUMMARY, params)
      console.log('✅ Stock alerts summary fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching stock alerts summary:', error)
      throw error
    }
  }

  // 🔍 Filter stock alerts by status
  filterByStatus(alerts, status) {
    if (!status || status === 'all') return alerts

    return alerts.filter(alert => alert.stockStatus === status)
  }

  // 🔍 Filter stock alerts by item type
  filterByItemType(alerts, itemType) {
    if (!itemType || itemType === 'all') return alerts

    return alerts.filter(alert => alert.itemType === itemType)
  }

  // 🔍 Filter stock alerts by branch
  filterByBranch(alerts, branchId) {
    if (!branchId || branchId === 'all') return alerts

    return alerts.filter(alert => {
      const alertBranchId = alert.branch?._id || alert.branch
      return alertBranchId === branchId
    })
  }

  // 🔍 Search stock alerts
  searchAlerts(alerts, searchTerm) {
    if (!searchTerm) return alerts

    const term = searchTerm.toLowerCase()
    return alerts.filter(alert => {
      const itemName = alert.name || ''
      const itemCode = alert.sku || alert.code || ''
      const branchName = alert.branch?.name || ''
      
      return itemName.toLowerCase().includes(term) ||
             itemCode.toLowerCase().includes(term) ||
             branchName.toLowerCase().includes(term)
    })
  }

  // 📊 Get stock status info
  getStockStatus(currentStock, threshold) {
    if (currentStock === 0) {
      return {
        status: 'out_of_stock',
        label: 'Out of Stock',
        color: 'red',
        priority: 'high'
      }
    } else if (currentStock <= threshold) {
      return {
        status: 'low_stock',
        label: 'Low Stock',
        color: 'yellow',
        priority: 'medium'
      }
    } else {
      return {
        status: 'normal',
        label: 'Normal',
        color: 'green',
        priority: 'low'
      }
    }
  }

  // 📊 Get status color classes
  getStatusColor(status) {
    switch (status) {
      case 'out_of_stock':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'low_stock':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-green-100 text-green-800 border-green-200'
    }
  }

  // 📊 Get status badge classes
  getStatusBadgeColor(status) {
    switch (status) {
      case 'out_of_stock':
        return 'bg-red-100 text-red-800'
      case 'low_stock':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-green-100 text-green-800'
    }
  }

  // 🔄 Sort stock alerts
  sortAlerts(alerts, sortBy = 'name', sortOrder = 'asc') {
    return [...alerts].sort((a, b) => {
      let valueA, valueB

      // Handle nested properties
      if (sortBy.includes('.')) {
        const keys = sortBy.split('.')
        valueA = keys.reduce((obj, key) => obj?.[key], a)
        valueB = keys.reduce((obj, key) => obj?.[key], b)
      } else {
        valueA = a[sortBy]
        valueB = b[sortBy]
      }
      
      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB?.toLowerCase() || ''
      }
      
      // Handle null/undefined values
      if (valueA == null) valueA = ''
      if (valueB == null) valueB = ''
      
      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }

  // 📊 Calculate alerts summary from data
  calculateSummary(alerts) {
    const summary = {
      totalItems: alerts.length,
      outOfStock: 0,
      lowStock: 0,
      products: 0,
      rawMaterials: 0,
      byBranch: {},
      criticalItems: []
    }

    alerts.forEach(alert => {
      switch (alert.stockStatus) {
        case 'out_of_stock':
          summary.outOfStock++
          break
        case 'low_stock':
          summary.lowStock++
          break
      }

      switch (alert.itemType) {
        case 'product':
          summary.products++
          break
        case 'raw_material':
          summary.rawMaterials++
          break
      }

      // Count by branch
      const branchId = alert.branch?._id || 'unknown'
      const branchName = alert.branch?.name || 'Unknown Branch'
      if (!summary.byBranch[branchId]) {
        summary.byBranch[branchId] = {
          name: branchName,
          count: 0,
          outOfStock: 0,
          lowStock: 0
        }
      }
      summary.byBranch[branchId].count++
      if (alert.stockStatus === 'out_of_stock') {
        summary.byBranch[branchId].outOfStock++
      } else if (alert.stockStatus === 'low_stock') {
        summary.byBranch[branchId].lowStock++
      }

      // Add critical items (out of stock)
      if (alert.stockStatus === 'out_of_stock') {
        summary.criticalItems.push({
          id: alert._id,
          name: alert.name,
          code: alert.sku || alert.code,
          type: alert.itemType,
          branch: alert.branch?.name
        })
      }
    })

    return summary
  }

  // 🎯 Get priority alerts (out of stock first, then low stock)
  getPriorityAlerts(alerts) {
    return alerts.sort((a, b) => {
      const priorityOrder = { 'out_of_stock': 3, 'low_stock': 2, 'normal': 1 }
      return priorityOrder[b.stockStatus] - priorityOrder[a.stockStatus]
    })
  }

  // 📈 Get trend data (if historical data is available)
  getAlertsTrend(alertsHistory) {
    // This would be used if you have historical stock alert data
    // For now, return empty trend data
    return {
      weekly: [],
      monthly: [],
      categories: []
    }
  }
}

// Export singleton instance
export default new StockAlertsService()